import {
  Icon,
  Link,
  Stack,
  mergeStyles,
  styled,
} from '@fluentui/react';
import * as React from 'react';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StyledText } from '../../../../web/common/components/StyledText';
import { appContext } from '../../../../web/models/getHelp/appContext';
import { resultHeaderStyle } from '../../../../web/self-help/styles/selfHelpStyles';
import { getRandomGuidString } from '../../../../web/utils/getHelputils';
import { captureContentUpdate, capturePageAction } from '../../../../web/utils/oneDsUtil';
import { AutomationIds } from '../../..//models/constants/automationIds';
import {
  AutoSuggestResult,
} from '../../../models/apiModels';
import { getCorrelationId, isDarkThemeSelector, isGetHelpSelector, showAutoSuggestCardSelector, autoSuggestStateSelector, selfHelpAlchemyStateSelector, sharedStateSelector } from '../../app/selectors/contactUsSelector';
import { setOriginalQuery } from '../reduxUpdater/actions/autoSuggestActions';
import { setRelatedCorrelationIds } from '../reduxUpdater/actions/selfHelpAlchemyActions';
import {
  formSubmission,
  setIsAutoSuggestCardSearch,
} from '../reduxUpdater/actions/sharedStateActions';
import { dispatchAlchemyFetch } from '../reduxUpdater/dispatchers/contactUsDispatchers';
import {
  ICommonThemedStyles,
  getCommonThemedClassNames,
  getCommonThemedStyles,
} from '../styles/commonStyles';
import {
  getAutoSuggestCardClassNames,
  getAutoSuggestCardLinkStyles,
  getAutoSuggestCardStyles,
  getHelpSearchRelatedQueriesLinkStyle,
  RelatedQueriesContainerStyle,
  RelatedQueriesSearchIconStyle,
} from '../styles/selfHelpStyles';
import { IStyledComponent } from '../styles/themesUI';
import { getString } from '../../../../web/utils/stringLoader';

type IAutoSuggestCardProps = IStyledComponent<ICommonThemedStyles>;

const AutoSuggestCardC = (props: IAutoSuggestCardProps) => {
  const autoSuggestState = useSelector(autoSuggestStateSelector);
  const selfHelpAlchemyState = useSelector(selfHelpAlchemyStateSelector);
  const sharedState = useSelector(sharedStateSelector);
  const isGetHelp = useSelector(isGetHelpSelector);
  const showAutoSuggestCard = useSelector(showAutoSuggestCardSelector);
  const isDarkTheme = useSelector(isDarkThemeSelector);
  const correlationId = useSelector(getCorrelationId);

  const dispatch = useDispatch();
  const dispatchFormSubmission = useCallback((query: string) => dispatch(formSubmission(query)), [dispatch]);
  const dispatchDispatchAlchemyFetch = useCallback((title: string) => dispatch(dispatchAlchemyFetch(title)), [dispatch]);
  const dispatchSetOriginalQuery = useCallback((originalQuery: string) => dispatch(setOriginalQuery(originalQuery)), [dispatch]);
  const dispatchSetIsAutoSuggestCardSearch = useCallback((isAutoSuggestCardSearch: boolean, autoSuggestResults?: AutoSuggestResult[]) => dispatch(setIsAutoSuggestCardSearch(isAutoSuggestCardSearch, autoSuggestResults)), [dispatch]);
  const dispatchSetRelatedCorrelationIds = useCallback(() => dispatch(setRelatedCorrelationIds()), [dispatch]);

  let relatedQueriesRef: React.RefObject<HTMLDivElement>;

  useEffect((): void => {
    relatedQueriesRef = React.createRef<HTMLDivElement>();
    /**
    * AutoSuggestCard meta: "ms.correlationId" is not updated for AutoSuggestCard logs when we click on AutoSuggestCard links
    * We're intentionally leaving it this way for the moment, until we find a better way to aggregate impressions
    */
    const logHandlerTags = {
      content: {
        contentId: 'AutoSuggestCardShown',
        contentName: 'AutoSuggestCardShown',
        correlationId: correlationId,
        formnm: 'maxFunnel',
        fileName: 'AutoSuggestCard.tsx',
        methodName: 'componentDidMount',
        areaName: 'formQuerySubmit',
        selfhelpprovider: 'alchemy',
        actionType: 'KE',
        relatedSHCorrelationIds: selfHelpAlchemyState.relatedSHCorrelationIds,
      },
      behavior: 'SHOW',
    };
    captureContentUpdate(logHandlerTags);
  }, []);

  useEffect((): void => {
    if (
      !selfHelpAlchemyState.isAlchemyQueryInProgress &&
      showAutoSuggestCard
    ) {
      dispatchSetRelatedCorrelationIds();
    }
  }, [selfHelpAlchemyState.isAlchemyQueryInProgress]);

  const onAutoSuggestClick = useCallback((query: string) => () => {
    /**
     * AutoSuggestCard meta: "ms.correlationId" is not updated for AutoSuggestCard logs when we click on AutoSuggestCard links
     * We're intentionally leaving it this way for the moment, until we find a better way to aggregate impressions
     */
    appContext.sessionId = getRandomGuidString();
    const logHandlerTags = {
      content: {
        contentId: 'onAutoSuggestClick',
        contentName: 'onAutoSuggestClickBtn',
        correlationId: getCorrelationId,
        formnm: 'maxFunnel',
        fileName: 'AutoSuggestCard.tsx',
        methodName: 'onAutoSuggestClick',
        areaName: 'formQuerySubmit',
        srchq: query,
        selfhelpprovider: 'alchemy',
        actionType: 'KE',
        relatedSHCorrelationIds: selfHelpAlchemyState.relatedSHCorrelationIds,
      },
      behavior: 'CLICK',
    };
    capturePageAction(null, logHandlerTags);
    dispatchSetOriginalQuery(query);
    dispatchSetIsAutoSuggestCardSearch(true);
    dispatchFormSubmission(query);
    dispatchDispatchAlchemyFetch(query);
  }, []);

  const queries = autoSuggestState.autoSuggestCardResultsV2?.suggestions;
  const contactSearchedQuery = sharedState.contactSearchedQuery || '';
  const keyPrefix = 'related_query_';
  const commonThemedClassNames = getCommonThemedClassNames(getCommonThemedStyles, { theme: props.theme, isDarkTheme: isDarkTheme });
  const autoSuggestClassNames = getAutoSuggestCardClassNames(
    getAutoSuggestCardStyles,
    {
      theme: props.theme,
      transparentBackground: isGetHelp,
    }
  );

  let queryLinks: JSX.Element[] | null;
  if (!!autoSuggestState.isAutoSuggestV2Enabled && queries && queries.length > 0) {
    queryLinks = queries.map((suggestion: string, index: number) => {
      return contactSearchedQuery.toLowerCase() ===
        suggestion.toLowerCase() ? null : (
        <Stack className={autoSuggestClassNames.linkContainer}>
          <Link
            // eslint-disable-next-line security/detect-non-literal-fs-filename
            styles={isGetHelp ? getHelpSearchRelatedQueriesLinkStyle(props.theme.semanticColors.link) : getAutoSuggestCardLinkStyles(props)}
            key={`${keyPrefix}${index}`}
            data-key={`${keyPrefix}${index}`}
            data-index={index}
            onClick={onAutoSuggestClick(suggestion)}
            data-automation-id={`${AutomationIds.AutoSuggestCardLink}-${index}`}
          >
            <Icon iconName="Search" styles={RelatedQueriesSearchIconStyle} />
            {suggestion}
          </Link>
        </Stack>
      );
    });
  }

  return (
    <>
      {queryLinks && (
        <Stack className={isGetHelp ? mergeStyles(RelatedQueriesContainerStyle(props.theme.palette.neutralLighterAlt)) : autoSuggestClassNames.root}>
          <StyledText className={isGetHelp ? mergeStyles(resultHeaderStyle.root) : commonThemedClassNames.header}>{getString('result_autoSuggestCardHeader')}</StyledText>
          <div aria-role="rowgroup" aria-label={getString('result_autoSuggestCardHeader')} className={autoSuggestClassNames.list} ref={relatedQueriesRef}>{queryLinks}</div>
        </Stack>
      )}
    </>
  );
};

export const AutoSuggestCard = styled(AutoSuggestCardC, getCommonThemedStyles);
